import { type FieldStat } from '@solink/vd-data-layer/api-clients/platform/platform';
import { type EventStandardDefs } from '@solinkcorp/event-standard';
import {
  addLocationTagsToQuery,
  ALL_LOCATIONS,
  ElasticSearchQuery as ESQuery,
  EVENT_SUBTYPE_DOWNLOAD,
  HISTOGRAM_RANGE,
  type HistogramRange,
  SEARCH_OPERATOR,
  type SearchOperator,
} from '@solinkcorp/vd-common';
import PlatformApi, { type EventQueryParameters } from 'clients/PlatformApi';
import {
  EventStandardFieldType,
  FieldStatFieldType,
  START_END_DATE_SEPARATOR,
} from 'components/EventFilter/utils';
import { type FetchEventsParams } from 'components/HomePage/types';
import forEach from 'lodash/forEach';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import { type FieldStats, getNoPrefixFieldsDict } from 'reducers/fieldStatsReducer';
import { AuthStore } from 'stores/AuthStore';
import EventFilterStore, { DEFAULT_EVENT_FITLERS } from 'stores/EventFilterStore';
import { LocationStore } from 'stores/LocationStore';
import { type EventFilterState } from 'utils/types';
import {
  CHECK_N_GO,
  DELIVERY_EVENT_SUBTYPE,
  DELIVERY_EVENT_SUBTYPE_ALL,
  GOLDEN_1,
  HIDE_FROM_DISPLAY_TAG,
  QUERY_COUNT,
  TABLEVIEW_DEFAULT_SORTING,
} from 'vd-constants';
import * as DashboardUtil from './DashboardUtil';

// TODO: Refactor this functions don't rely on any stores.  If values in stores change,
// then components which subscribe to those values should pass in the new values;
// this way we are guaranteed that components will update themselves when the values
// they rely upon change.

const OPERATORS = {
  '>': 'gt',
  '>=': 'gte',
  '<=': 'lte',
  '<': 'lt',
  gt: 'gt',
  gte: 'gte',
  lte: 'lte',
  lt: 'lt',
  between: 'between',
  on: 'on',
  contains: 'contains',
  doesNotContain: 'doesNotContain',
} as const;

export type ESMustTerm =
  | { term: { [key: string]: number | string } }
  | { range: { [key: string]: { [val in FilterOperator]?: number | string } } };

export type FilterOperator =
  | 'is'
  | 'is not'
  | 'isNot'
  | '='
  | 'ne'
  | '>'
  | '>='
  | '<='
  | '<'
  | 'gt'
  | 'gte'
  | 'lte'
  | 'lt'
  | 'between'
  | 'on'
  | 'contains'
  | 'doesNotContain';

type FilterBaseValue = string | number | boolean;
type FilterValue = FilterBaseValue | [FilterBaseValue, FilterBaseValue];
export interface Filter {
  filterProp: string;
  filterVal: FilterValue;
  filterLogic: FilterOperator;
}

export interface SearchCapsule extends Filter {
  capsule: string;
}

export function filterToString(
  eventStandard: EventStandardDefs,
  localeCode: string,
  filter: Filter,
) {
  return `${DashboardUtil.prettifyTerm(eventStandard, filter.filterProp, localeCode)} ${
    filter.filterLogic
  } ${filter.filterVal}`;
}

const VALID_RANGES: HistogramRange[] = ['Day', 'Week', 'Month', 'Year'];

export function histogramRangeToMomentDuration(range: HistogramRange) {
  return range.toLowerCase() as moment.unitOfTime.DurationConstructor;
}

/**
 * Given a range like "day" or "Day", returns the canonical HistogramRange (e.g. "Day"),
 * or undefined if the passed in range is invalid.
 */
export function validateHistogramRange(range: string | undefined): HistogramRange | undefined {
  if (!range) {
    return undefined;
  }
  if (VALID_RANGES.includes(range as HistogramRange)) {
    return range as HistogramRange;
  }
  return VALID_RANGES.find((validRange) => validRange.toLowerCase() === range.trim().toLowerCase());
}

function isFilterOperator(val: string): val is FilterOperator {
  return (
    val === 'is' ||
    val === 'is not' ||
    val === 'isNot' ||
    val === '=' ||
    val === 'ne' ||
    val === 'contains' ||
    val === 'doesNotContain' ||
    val in OPERATORS
  );
}

export const getQueryRange = (params: {
  range?: HistogramRange;
  selectedReportId?: string;
  eventFilterState?: EventFilterState;
}) => {
  const { range, selectedReportId, eventFilterState } = params;
  const customer = AuthStore.getCustomer();
  const { transaction, motion, bookmark } = eventFilterState || DEFAULT_EVENT_FITLERS;
  const hasTransactions = transaction;
  const motionMain = motion && !transaction;
  const bookmarkOnly = bookmark && !transaction && !motion;
  const customerName = get(customer, 'name');
  const specialCustomers = customerName === CHECK_N_GO || customerName === GOLDEN_1;
  const isRangeDay = range === HISTOGRAM_RANGE.DAY;
  const isRangeWeek = range === HISTOGRAM_RANGE.WEEK;
  const isRangeMonth = range === HISTOGRAM_RANGE.MONTH;
  const isRangeYear = range === HISTOGRAM_RANGE.YEAR;
  const hourUnit = 'hours' as const;
  const dayUnit = 'days' as const;

  // TODO: do we need to consider location filtering in the future?

  if (motionMain && isRangeDay) {
    return {
      value: 48,
      unit: hourUnit,
    };
  }

  if (
    specialCustomers ||
    (hasTransactions && (isRangeDay || (isRangeWeek && !selectedReportId))) ||
    (motionMain && isRangeWeek)
  ) {
    return {
      value: 7,
      unit: dayUnit,
    };
  }

  if (hasTransactions && selectedReportId && isRangeWeek) {
    return {
      value: 15,
      unit: dayUnit,
    };
  }

  if (hasTransactions && selectedReportId && isRangeMonth) {
    return {
      value: 60,
      unit: dayUnit,
    };
  }

  if (bookmarkOnly || isRangeYear) {
    return {
      value: 365,
      unit: dayUnit,
    };
  }

  // For all other cases, use 32 days as range
  return {
    value: 32,

    unit: dayUnit,
  };
};

function sanitizeSortById(sortById: string) {
  // title and subtitle are text fields, not keyword fields.
  // so we need to append .keyword to sort by them
  if (sortById === 'title' || sortById === 'subtitle') {
    return sortById + '.keyword';
  }
  if (sortById === 'startTimeLocal') {
    return 'startTime';
  }
  return sortById;
}

export const fetchEventsForTimeAndLocation = async ({
  eventFilterState,
  searchString,
  searchFilters,
  tableRowPerPage,
  startTime,
  endTime,
  locationId,
  locationIds = [],
  offset = 0,
  reportTemplateId = undefined,
  sortBy = TABLEVIEW_DEFAULT_SORTING,
  eqFilter = {},
  eqShouldFields = {},
  eqMustHaveFields = {},
  eqMustNonZeroFields = [],
  includeTypes = false,
  include,
  source,
}: FetchEventsParams) => {
  const sortById = sanitizeSortById(sortBy.prop);
  const selectedReportId =
    reportTemplateId || EventFilterStore.getSelectedReportId({ checkInitial: true });

  const params = generateQueryParameters({
    start: new Date(startTime).toISOString(),
    end: new Date(endTime).toISOString(),
    locationId,
    locationIds,
    count: tableRowPerPage,
    offset,
    eqFilter,
    eqShouldFields,
    eqMustHaveFields,
    eqMustNonZeroFields,
    includeTypes,
    include,
    searchString,
    searchFilters,
    eventFilterState,
    selectedReportId,
    sorts: [{ [sortById]: { order: sortBy.isDescending ? 'desc' : 'asc' } }],
    source,
  });

  const { events, total } = await getEventsFromPlatform(params);

  return {
    events,
    eventsChangeTime: new Date().toISOString(),
    totalTableEvents: total,
  };
};

export const getEventsFromPlatform = async (params: EventQueryParameters) => {
  const { results, total } = await PlatformApi().listEvents(params);
  if (results) {
    return {
      events: results,
      total,
    };
  }
  // TODO: Do we ever reach this case?
  return {
    events: [],
    total: 0,
  };
};

export const applyFilterToElasticSearchQuery = ({
  searchEq,
  filter,
  filterSearchWithShould,
}: {
  searchEq: ESQuery;
  filter: Filter;
  filterSearchWithShould: boolean;
}) => {
  const { filterProp, filterLogic, filterVal } = filter;
  if (filterLogic === 'is') {
    if (filterSearchWithShould) {
      searchEq.should(filterProp, filterVal);
    } else {
      searchEq.must(filterProp, filterVal);
    }
  } else if (filterLogic === 'is not' || filterLogic === 'isNot') {
    if (filterSearchWithShould) {
      searchEq.shouldQuery({ bool: { must_not: { term: { [filterProp]: filterVal } } } });
    } else {
      searchEq.must_not(filterProp, filterVal);
    }
  } else if (filterLogic === '=') {
    let queryParam = filterVal;
    if (typeof queryParam === 'string' && queryParam.startsWith('"') && queryParam.endsWith('"')) {
      queryParam = queryParam.substring(1, queryParam.length - 1);
    }
    if (filterSearchWithShould) {
      searchEq.shouldQuery({
        range: {
          [filterProp]: {
            gte: queryParam,
            lte: queryParam,
          },
        },
      });
    } else {
      searchEq.range(filterProp, {
        gte: queryParam,
        lte: queryParam,
      });
    }
  } else if (filterLogic === 'ne') {
    if (filterSearchWithShould) {
      searchEq.shouldQuery({ bool: { must_not: { term: { [filterProp]: filterVal } } } });
    } else {
      searchEq.must_not(filterProp, filterVal, false);
    }
  } else if ((filterLogic === 'between' || filterLogic === 'on') && filterVal instanceof Array) {
    if (filterSearchWithShould) {
      searchEq.shouldQuery({
        range: {
          [filterProp]: {
            gte: filterVal[0],
            lte: filterVal[1],
          },
        },
      });
    } else {
      searchEq.range(filterProp, {
        gte: filterVal[0],
        lte: filterVal[1],
      });
    }
  } else if (filterLogic === 'contains' || filterLogic === 'doesNotContain') {
    searchEq.must('query_string', {
      default_field: filterProp.replace('.keyword', ''),
      query: filterVal as string,
    });
  } else if (OPERATORS[filterLogic]) {
    if (filterSearchWithShould) {
      searchEq.shouldQuery({
        range: {
          [filterProp]: {
            [OPERATORS[filterLogic]]: filterVal,
          },
        },
      });
    } else {
      searchEq.range(filterProp, {
        [OPERATORS[filterLogic]]: filterVal,
      });
    }
  } else {
    throw new Error(`Unknown filterLogic: ${filterLogic}`);
  }
};

// Note if you modify this data structure, you also want to update:
//
// * EventCache#eventClientToEventCacheQuery() and the EventCache.
// * EventFetchUtil#isEmptyQuery()
// * EventFetchUtil#generateQueryParameters()
//
export interface BasicEventQueryParameters {
  locationId?: string;
  /** locationIds to filter.  If specified, this will take precedence over locationId. */
  locationIds?: readonly string[];
  eventFilterState?: EventFilterState;
  eventSubtypeFilter?: { [subtype: string]: boolean };
  eventTagsFilter?: string[];
  filters?: readonly Filter[];
  eqFilter?: { [key: string]: any };
  eqShouldFields?: { [key: string]: any };
  eqMustHaveFields?: { [key: string]: any };
  eqMustNonZeroFields?: readonly string[];
  eqMustNotFields?: any;
  selectedReportId?: string;
  includeTypes?: boolean;
  /** If specified, only match events with the given subtitle. */
  subtitle?: string;
  /** Optional string to ask Platform for specific types of events. */
  include?: string;
  searchString?: string;
  searchFilters?: readonly SearchCapsule[];
  searchOperator?: SearchOperator;
}

export interface GenerateQueryParametersParameters extends BasicEventQueryParameters {
  start?: string;
  end?: string;
  tableRowPerPage?: number;
  sortOrder?: 'asc' | 'desc';
  count?: number;
  offset?: number;
  reportTemplateId?: string;
  eqMustExistsFields?: readonly string[];
  fields?: readonly string[];
  timeFieldToSearchOn?: string;
  sorts?: { [key: string]: { order: 'asc' | 'desc' } }[];
  minimumShouldMatch?: number;
  next?: unknown[];
  source?: string;
  totalLimit?: boolean;
}

/**
 * If a query restricts which locations to return results from, this will return
 * the list of location IDs.
 */
export function getQueryLocationIds(
  query: Pick<BasicEventQueryParameters, 'locationIds' | 'locationId'>,
): readonly string[] | undefined {
  if (query.locationIds) {
    return query.locationIds;
  } else if (query.locationId && query.locationId !== ALL_LOCATIONS) {
    return [query.locationId];
  }
  return [];
}

export const generateQueryParameters = ({
  start,
  end,
  locationId,
  locationIds,
  sortOrder = 'desc',
  count = QUERY_COUNT,
  offset = 0,
  eqFilter = {},
  eqShouldFields = {},
  eqMustExistsFields = [],
  eqMustHaveFields = {},
  eqMustNonZeroFields = [],
  eqMustNotFields = {},
  fields,
  includeTypes = true,
  eventFilterState,
  eventSubtypeFilter,
  eventTagsFilter,
  filters,
  selectedReportId,
  timeFieldToSearchOn = 'startTime',
  sorts,
  subtitle,
  include,
  searchString,
  minimumShouldMatch,
  next,
  source,
  searchOperator,
  totalLimit,
}: GenerateQueryParametersParameters) => {
  const params: EventQueryParameters = {
    count,
    timeFieldToSearchOn,
    sorts: sorts || [{ startTime: { order: sortOrder } }, { created: { order: sortOrder } }],
    source,
    totalLimit,
    next,
    ...(fields ? { fields } : {}),
    ...(start ? { start } : {}),
    ...(end ? { end } : {}),
  };

  if (include) {
    params.include = include;
  }

  if (!next) {
    params.offset = offset;
  }

  if (selectedReportId) {
    params.reportTemplateId = selectedReportId;
  }

  const eq = getQueryParameters({
    eventFilterState,
    eventSubtypeFilter,
    eventTagsFilter,
    searchString,
    filters,
    locationId,
    locationIds,
    query: {},
    includeTypes,
    eqFilter,
    eqShouldFields,
    eqMustExistsFields,
    eqMustHaveFields,
    eqMustNonZeroFields,
    eqMustNotFields,
    subtitle,
    minimumShouldMatch,
    searchOperator,
  });

  // TODO: this part seems duplicate with the functions in _getQueryParametersDashboardTriggered, can write something reusable?
  const restrictedCameras = LocationStore.getRestrictedCameras();
  if (restrictedCameras && restrictedCameras.length > 0) {
    eq.must('terms', restrictedCameras, 'primaryCameraId');
  }

  Object.assign(params, eq.toFilter().bool);

  return params;
};

export const getQueryParameters = ({
  eventFilterState,
  eventSubtypeFilter,
  eventTagsFilter,
  searchString,
  filters,
  locationId,
  locationIds,
  cameraIds,
  locationTags,
  query = {},
  includeTypes = true,
  eqFilter = {},
  eqShouldFields = {},
  eqMustExistsFields = [],
  eqMustHaveFields = {},
  eqMustNonZeroFields = [],
  eqMustNotFields = {},
  subtitle,
  minimumShouldMatch,
  searchOperator,
}: {
  eventFilterState?: EventFilterState;
  eventSubtypeFilter?: { [subtype: string]: boolean };
  eventTagsFilter?: string[];
  searchString?: string;
  filters?: readonly Filter[];
  locationId?: string;
  locationIds?: readonly string[];
  locationTags?: readonly string[];
  query?: any;
  includeTypes?: boolean;
  eqFilter?: { [key: string]: any };
  eqShouldFields?: { [key: string]: any };
  eqMustExistsFields?: readonly string[];
  eqMustHaveFields?: { [key: string]: any };
  eqMustNonZeroFields?: readonly string[];
  eqMustNotFields?: { [key: string]: any };
  subtitle?: string;
  minimumShouldMatch?: number;
  searchOperator?: SearchOperator;
  cameraIds?: string[];
}) => {
  const { saved } = eventFilterState || {};

  const minimumShouldMatchCalculated = minimumShouldMatch ?? getMinimumShouldMatch(saved);

  const eq = new ESQuery(query);

  if (includeTypes && eventFilterState) {
    Object.keys(eventFilterState).forEach((f) => {
      const filter = f as keyof typeof eventFilterState;
      if (eventFilterState[filter] && filter !== 'saved') {
        eq.should('type', filter, minimumShouldMatchCalculated);
      }
    });

    if (saved) {
      eq.should('subtype', EVENT_SUBTYPE_DOWNLOAD, minimumShouldMatchCalculated);
      eq.shouldExists('shareStartTime', minimumShouldMatchCalculated);
    }
  }

  applySubtypeFilter(eq, eventSubtypeFilter);
  applyTagsFilter(eq, eventTagsFilter);

  addSearchToQuery({
    eq,
    searchString,
    searchFilters: filters,
    searchOperator,
  });

  if (locationTags?.length) {
    addLocationTagsToQuery({ locationTags, eq });
  } else if (locationIds?.length && !locationIds.includes(ALL_LOCATIONS)) {
    if (locationIds.length > 1) {
      eq.filter({ terms: { siteId: [...locationIds] } });
    } else {
      eq.filter({ term: { siteId: locationIds[0] } });
    }
  } else if (locationId && locationId !== ALL_LOCATIONS) {
    eq.filter({ term: { siteId: locationId } });
  }

  if (cameraIds?.length) {
    eq.filter({
      nested: {
        path: 'cameras',
        query: {
          terms: {
            'cameras.id': cameraIds,
          },
        },
      },
    });
  }

  if (!isEmpty(eqShouldFields)) {
    forEach(eqShouldFields, (value, key) => {
      eq.should(key, value, minimumShouldMatchCalculated);
    });
  }

  if (!isEmpty(eqMustExistsFields)) {
    eqMustExistsFields.forEach((key: string) => {
      eq.mustExists(key);
    });
  }

  if (!isEmpty(eqMustHaveFields)) {
    for (const [key, value] of Object.entries(eqMustHaveFields)) {
      if (typeof value !== 'undefined') {
        eq.must(key, value);
      }
    }
  }

  if (!isEmpty(eqMustNotFields)) {
    forEach(eqMustNotFields, (value, key) => {
      if (value) {
        eq.must_not(key, value);
      }
    });
  }

  if (!isEmpty(eqMustNonZeroFields)) {
    eqMustNonZeroFields.forEach((key: string) => {
      eq.mustExists(key);
      eq.must_not(key, 0);
    });
  }

  if (!isEmpty(eqFilter)) {
    eq.filter(eqFilter);
  }

  if (subtitle) {
    eq.must('subtitle.keyword', subtitle);
  }

  return eq;
};

/**
 * Applies a subtype filter to an ES Query.
 *
 * Note that modifications to this function should be reflected below in `checkEventAgainstSubtypeFilter`.
 */
export function applySubtypeFilter(
  eq: ESQuery,
  eventSubtypeFilter: { [subtype: string]: boolean } | undefined,
) {
  if (!eventSubtypeFilter || Object.keys(eventSubtypeFilter).length === 0) {
    return;
  }

  addSubtypesToQuery(
    eq,
    Object.keys(eventSubtypeFilter).filter((subtype) => !!eventSubtypeFilter[subtype]),
  );
}

/**
 * Adds all the event tags defined in the feature standard to the filter in the ES Query.
 */
export function applyTagsFilter(eq: ESQuery, eventTags?: readonly string[]) {
  if (eventTags) {
    eventTags.forEach((tag) => {
      addTagToQuery(eq, tag);
    });
  }
}

/**
 * Checks to see if an event matches a subtype filter.
 *
 * Note that modifications to this function should be reflected above in `applySubtypeFilter`.
 */
export function checkEventAgainstSubtypeFilter(
  event: { subtype?: string },
  eventSubtypeFilter: { [subtype: string]: boolean } | undefined,
) {
  if (!eventSubtypeFilter) {
    return true;
  }

  return Object.keys(eventSubtypeFilter).every((subtype) => {
    if (!eventSubtypeFilter[subtype]) {
      return true;
    }
    //
    // Special case for 3pd
    if (subtype === DELIVERY_EVENT_SUBTYPE_ALL) {
      return event.subtype ? event.subtype.startsWith(DELIVERY_EVENT_SUBTYPE) : false;
    }
    // Either the same filter is applied or a wildcard filter is applied
    // Both cases should return the event
    return (
      event.subtype === subtype ||
      (subtype.endsWith('.*') && event.subtype?.startsWith(subtype.split('.')[0]))
    );
  });
}

export function addSearchToQuery({
  eq,
  searchString,
  searchFilters,
  searchOperator,
}: {
  eq: ESQuery;
  searchString: string | undefined;
  searchFilters: readonly Filter[] | undefined;
  searchOperator?: SearchOperator;
}) {
  const searchEq = new ESQuery();

  const filterSearchWithShould = getFilterSearchUsingShould({
    searchFilters,
    searchString,
    searchOperator,
  });

  if (searchString?.length) {
    if (filterSearchWithShould) {
      searchEq.should('multi', searchString);
    } else {
      searchEq.must('multi', searchString);
    }
  }

  if (searchFilters?.length) {
    for (const filter of searchFilters) {
      applyFilterToElasticSearchQuery({
        searchEq,
        filter,
        filterSearchWithShould,
      });
    }
  }

  if (Object.keys(searchEq.toFilter().bool).length) {
    eq.filter(searchEq.toFilter());
  }
}

/**
 * Add one or several subtype(s) to the es query.
 * Do not call this method twice, as it would never be able to return any data.
 * If calling this method multiple times, the query will request events that have ALL the provided subtypes.
 * Instead, use `addSubtypesToQuery` (plural form) which will request events that have ANY of the provided subtypes.
 * @param eq es query
 * @param subtype subtype
 */
export function addSubtypesToQuery(eq: ESQuery, subtypes: string[]) {
  if (subtypes.some((subtype) => subtype.endsWith('.*'))) {
    eq.filter({ regexp: { subtype: { value: subtypes.join('|') } } });
  } else if (subtypes.length === 1) {
    eq.filter({ term: { subtype: subtypes[0] } });
  } else {
    eq.filter({ terms: { subtype: subtypes } });
  }
}

export function addTagToQuery(eq: ESQuery, tag: string) {
  eq.filter({ term: { tags: tag } });
}

export function parseSearchCapsules(
  eventStandard: EventStandardDefs,
  fieldStats: FieldStats,
  capsules: readonly string[] | undefined,
): SearchCapsule[] | undefined {
  const result: SearchCapsule[] =
    capsules?.flatMap((capsule) => {
      const parsedCapsule = parseSearchCapsule(eventStandard, fieldStats, capsule, 'details.');
      return parsedCapsule ? [parsedCapsule] : [];
    }) || [];

  return result?.length ? result : undefined;
}

export function parseSearchCapsule(
  eventStandard: EventStandardDefs,
  fieldStats: FieldStats,
  capsule: string,
  prefix: string = '',
): SearchCapsule | undefined {
  const qs = capsule.split('&nbsp;');
  const filterLogic = qs[1];

  if (!isFilterOperator(filterLogic)) {
    console.warn(`Ignoring invalid capsule: ${capsule}`);
    return undefined;
  }

  //
  // To do: must be a better way to do this
  // TODO: after we get rid of the legacy Search and FSSearch, we shouldn't need to parse the event field name.
  //       the field name should contains prefix, this function should just use the name passed in from capsule.
  //       also check the function that parse the share link of the events page that contains search capsules,
  //       the capsules parsed out from the URL should use the raw name of the field as well.
  const noPrefixFieldsDict = getNoPrefixFieldsDict(fieldStats);
  const doNotAddPrefix = noPrefixFieldsDict[qs[0]];

  const filterProp = doNotAddPrefix ? qs[0] : prefix + `${qs[0]}`;
  let filterVal: FilterValue = qs[2];
  const field =
    eventStandard[filterProp.replace('.keyword', '')] ||
    fieldStats.find((fieldStat: FieldStat) => fieldStat.name === filterProp);
  if (field?.type === EventStandardFieldType.BOOLEAN) {
    filterVal = filterVal === 'true';
  }
  if (
    (field?.type === EventStandardFieldType.DATE_TIME ||
      (field as FieldStat)?.type === FieldStatFieldType.DATE) &&
    typeof filterVal === 'string'
  ) {
    if (filterLogic === 'between' || filterLogic === 'on') {
      filterVal = filterVal
        .split(START_END_DATE_SEPARATOR)
        .map((val) => new Date(parseInt(val)).toISOString()) as [string, string];
    } else {
      filterVal = new Date(parseInt(filterVal)).toISOString();
    }
  }

  return {
    capsule,
    filterProp,
    filterLogic,
    filterVal,
  };
}

export const getTableRowsPerPage = () => {
  const MODAL_HEADER_HEIGHT = 92;
  const MODAL_BODY_MIN_HEIGHT = 900;
  const VIEW_HEIGHT = Math.max(document.documentElement.clientHeight, window.innerHeight || 0);
  const MODAL_HEIGHT = VIEW_HEIGHT * 0.9;
  const IS_SHORT_WINDOW = MODAL_HEIGHT - MODAL_HEADER_HEIGHT < MODAL_BODY_MIN_HEIGHT;

  return IS_SHORT_WINDOW ? 10 : 15;
};

/**
 * Returns true if the specified EventFilterState would return all available events.
 */
export function isDefaultEventFilters(eventFilters: EventFilterState | undefined) {
  return (
    !eventFilters ||
    (eventFilters.transaction &&
      eventFilters.motion &&
      eventFilters.bookmark &&
      !eventFilters.saved)
  );
}

/**
 * Returns true if the given EventClient query would return all available events
 * (ignoring locationId, locationIds, and includeTypes - fields that we
 * don't pass on to the event cache).
 */
export function isEmptyQuery(query: BasicEventQueryParameters | undefined): {
  isSearching: boolean;
  isEmpty: boolean;
} {
  if (!query) {
    return { isSearching: false, isEmpty: true };
  }

  const emptyEventFilters = isDefaultEventFilters(query.eventFilterState);

  const isSearching: boolean = !!query.searchString || !!query.searchFilters?.length;

  const isFiltering =
    query.eventSubtypeFilter ||
    query.filters?.length ||
    query.eqShouldFields ||
    query.eqMustHaveFields ||
    query.eqMustNonZeroFields ||
    query.eqMustNotFields ||
    query.selectedReportId ||
    query.subtitle;

  const isEmpty =
    !isSearching && !isFiltering && emptyEventFilters && query.include !== HIDE_FROM_DISPLAY_TAG;

  return { isSearching, isEmpty };
}

export function getMinimumShouldMatch(saved: boolean | undefined) {
  return saved ? 2 : 1;
}

export function getFilterSearchUsingShould({
  searchString,
  searchOperator,
  searchFilters,
}: {
  searchString: string | undefined;
  searchFilters: readonly Filter[] | undefined;
  searchOperator?: SearchOperator;
}) {
  // We should only filter events with the should clause if the following conditions are true
  // searchOperator === OR
  // There is a searchString + searchFilter OR there are 2 or more searchFilters
  return (
    ((!!searchString?.length && !!searchFilters?.length) ||
      (!!searchFilters?.length && searchFilters?.length > 1)) &&
    searchOperator === SEARCH_OPERATOR.OR
  );
}
